import { AlgoliaSuggestionsRequestParams } from '@/api/algolia';
import Autocomplete from '@/app/_components/autocomplete';
import { AutocompleteItemOption } from '@/app/_components/autocomplete/types';
import { ELTextField } from '@davincihealthcare/elty-design-system-react';
import { DEFAULT_AUTOCOMPLETE_HEALTCARE_SERVICES } from '@/costants';
import { useInclinicStore, useTantoSvagoStore } from '@/store';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useEffect, useMemo, useState } from 'react';
import { useFocus } from '@/app/_hooks/useFocus';
import { AUTOCOMPLETE as AUTOCOMPLETE_TYPE } from '@/costants/enum';
import { PaymentFilterType } from '@/api/algolia/types';

const fetchAlgoliaSuggestions = async (search: AlgoliaSuggestionsRequestParams, setSuggestionsCb: Function) => {
  if (!search.text) {
    return;
  }
  if (search.text.length < 3) {
    setSuggestionsCb([]);
    return;
  }
  import('@/api/algolia').then(async algolia => {
    setSuggestionsCb(await algolia?.getAlgoliaSearchSuggestions(search));
  });
};

type HealthcareServiceProps = React.ComponentPropsWithoutRef<'input'> & {
  setValue: (value: string) => void;
  inline?: boolean;
  onClick?: () => void;
  showCurrentTextInOptions?: boolean;
  onOptionClick: (value: AutocompleteItemOption) => void;
};

const HealthcareService = ({
  setValue,
  inline,
  value,
  id,
  onClick,
  onOptionClick,
  showCurrentTextInOptions = false,
}: HealthcareServiceProps) => {
  const { searchForm } = useInclinicStore(state => state);
  const [suggestions, setSuggestions] = useState<AutocompleteItemOption[]>([]);
  const isWelfare = useTantoSvagoStore();
  const [isAutocompleteOpen, setIsAutocompleteOpen] = useState(false);
  const [inputRef, setFocus] = useFocus<HTMLInputElement | null>();
  const [shouldOpenMenu, setShouldOpenMenu] = useState(false);

  const clearInput = () => {
    setValue('');
    setFocus();
    setShouldOpenMenu(true);
  };

  useEffect(() => {
    if (searchForm.healthcareService !== value && isAutocompleteOpen) {
      const search: AlgoliaSuggestionsRequestParams = {
        text: String(value),
        filterObj: {
          paymentType: isWelfare ? PaymentFilterType.online : undefined,
        },
      };

      const debounceTimeoutId = setTimeout(async () => {
        await fetchAlgoliaSuggestions(search, setSuggestions);
      }, 300);
      return () => {
        clearTimeout(debounceTimeoutId);
      };
    }
  }, [value, searchForm.healthcareService, isAutocompleteOpen, isWelfare]);

  const options = useMemo(() => {
    if (!String(value)) {
      return DEFAULT_AUTOCOMPLETE_HEALTCARE_SERVICES;
    }
    if (showCurrentTextInOptions) {
      return [...suggestions, { value: String(value), label: String(value), type: AUTOCOMPLETE_TYPE.SPECIALITY }];
    }
    return suggestions;
  }, [value, suggestions, showCurrentTextInOptions]);

  return (
    <Autocomplete
      shouldOpenMenu={shouldOpenMenu}
      setShouldOpenMenu={setShouldOpenMenu}
      options={options}
      setInputValueRef={setValue}
      onOptionClick={onOptionClick}
      inputValueRef={String(value)}
      variant={inline ? 'header' : 'page'}
      id={id || `autocomplete-${Date.now()}`}
      onOpenChange={setIsAutocompleteOpen}
    >
      {inline ? (
        <div className="relative inline-flex h-full w-full items-center text-xs text-slate-500">
          <input
            value={value}
            ref={inputRef}
            onClick={onClick}
            onChange={e => setValue(e.target.value)}
            placeholder="Ricerca prestazione/clinica"
            className="font-mendium flex h-full w-full items-center truncate rounded-l-6xl bg-gray-100 p-6 py-4 text-sm text-primary placeholder:text-[#627282] md:font-semibold"
            data-cy="input-searchbar"
          />
          {value && (
            // TODO to rework in button
            // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
            <span
              className="absolute right-2 hidden h-5 w-5 cursor-pointer text-primary peer-focus:text-primary-active md:block"
              onClick={clearInput}
              data-testid="clear-icon"
            >
              <XMarkIcon />
            </span>
          )}
        </div>
      ) : (
        <div className="w-full">
          <ELTextField
            name="service"
            innerRef={inputRef as React.RefObject<HTMLInputElement>}
            aria-label="service"
            placeholder="Ricerca prestazione/clinica"
            value={value}
            onClick={onClick}
            onChange={e => setValue(e.target.value)}
            leadingIcon={<MagnifyingGlassIcon />}
            data-cy="input-searchbar"
            onClear={value ? clearInput : undefined}
          />
        </div>
      )}
    </Autocomplete>
  );
};

export default HealthcareService;
